/**
 * InviteToWorkspaceModal Component
 *
 * Modal for inviting new team members to a workspace.
 * 
 * ACCESS MODEL (simplified):
 * - Workspace membership grants access to ALL properties attached to that workspace
 * - No per-user property selection needed
 * - Just collect email and role
 */

import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { WorkspaceRole } from "@/lib/types/workspace-team"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  EarthIcon,
  Loading01Icon,
  Mail01Icon,
} from "@hugeicons/core-free-icons"

interface InviteToWorkspaceModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaceName: string
  propertyCount: number
  onInvite: (email: string, role?: WorkspaceRole) => Promise<void>
  canInvite: boolean
  seatLimitReached?: boolean
  availableSeats?: number | string
}

const ROLE_DESCRIPTIONS: Record<WorkspaceRole, string> = {
  owner: "Full access, can manage settings and team",
  admin: "Can manage team and workspace settings",
  member: "Can view and interact with all data",
  viewer: "Read-only access to workspace data",
}

export default function InviteToWorkspaceModal({
  open,
  onOpenChange,
  workspaceName,
  propertyCount,
  onInvite,
  canInvite,
  seatLimitReached = false,
  availableSeats,
}: InviteToWorkspaceModalProps) {
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<WorkspaceRole>("member")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Email validation
  const isValidEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value)
  }

  const handleSubmit = async () => {
    setError(null)

    // Validate email
    if (!email.trim()) {
      setError("Please enter an email address")
      return
    }
    if (!isValidEmail(email.trim())) {
      setError("Please enter a valid email address")
      return
    }

    setIsSubmitting(true)
    try {
      await onInvite(email.trim(), role)
      // Reset form and close modal on success
      setEmail("")
      setRole("member")
      onOpenChange(false)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send invitation"
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail("")
      setRole("member")
      setError(null)
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Invite a teammate</DialogTitle>
          <DialogDescription>
            They join{" "}
            <span className="font-medium text-foreground">{workspaceName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Seat limit: a plan limit, not an error */}
          {seatLimitReached && (
            <Alert variant="warning" className="py-2">
              <AlertDescription className="text-xs">
                Every seat is taken. Upgrade or remove a member to invite someone.
              </AlertDescription>
            </Alert>
          )}

          {/* Available seats info */}
          {!seatLimitReached && availableSeats !== undefined && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="">Available</span>
              <span className="font-semibold text-foreground tabular-nums">
                {availableSeats === Infinity || availableSeats === "Unlimited"
                  ? "∞"
                  : availableSeats}
              </span>
              <span>seat{availableSeats !== 1 ? "s" : ""}</span>
            </div>
          )}

          {/* Email input */}
          <div className="space-y-1.5">
            <Label htmlFor="invite-email" className="text-xs font-medium">
              Email
            </Label>
            <div className="relative">
              <HugeiconsIcon icon={Mail01Icon} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError(null)
                }}
                disabled={isSubmitting || seatLimitReached}
                className="pl-8 h-9 text-sm"
              />
            </div>
          </div>

          {/* Role selection */}
          <div className="space-y-1.5">
            <Label htmlFor="invite-role" className="text-xs font-medium">
              Role
            </Label>
            <Select
              value={role}
              onValueChange={(value) => setRole(value as WorkspaceRole)}
              disabled={isSubmitting || seatLimitReached}
            >
              <SelectTrigger id="invite-role" className="h-9 w-full text-sm">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner" className="text-sm">Owner</SelectItem>
                <SelectItem value="member" className="text-sm">Member</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {ROLE_DESCRIPTIONS[role]}
            </p>
          </div>

          {/* Access info */}
          <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-muted/50 border border-border">
            <HugeiconsIcon icon={EarthIcon} size={14} className="text-foreground/60 shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Access to all <span className="font-medium">{propertyCount} site{propertyCount !== 1 ? "s" : ""}</span> in this workspace.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <Alert variant="destructive" className="py-2">
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="lg"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !canInvite ||
              seatLimitReached ||
              !email.trim()
            }
          >
            {isSubmitting ? (
              <>
                <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />
                Sending…
              </>
            ) : (
              "Send invite"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
